/**
 * Block Registry Implementation
 *
 * Map-based registry for block type configurations.
 * Provides O(1) lookup for block components, icons, and default content.
 *
 * Usage:
 * - Block configs are registered via registerBlock() at module load time
 * - BlockRenderer uses getBlockComponent() to resolve components
 * - Insert menus use getAddBlockMenuConfigs() for UI block lists
 */

import type { BlockType } from "@/canvas/types/blocks";
import type { BlockConfig } from "./types";

// Registry storage - Map for O(1) lookup by block type
const blockRegistry = new Map<BlockType, BlockConfig>();

/**
 * Register a block type configuration.
 *
 * @param config - The block configuration to register
 *
 * @example
 * registerBlock({
 *   type: "risks",
 *   component: RisksBlock,
 *   icon: AlertTriangle,
 *   label: "canvas.blockTypes.risks",
 *   defaultContent: () => ({ items: [] }),
 * });
 */
export function registerBlock<T extends BlockType>(config: BlockConfig<T>): void {
  if (blockRegistry.has(config.type)) {
    console.warn(`Block type "${config.type}" is already registered. Overwriting.`);
  }
  // Cast is safe: BlockConfig<T> is assignable to BlockConfig<BlockType> at runtime
  // TypeScript needs help here because T extends BlockType but isn't assignable to it
  blockRegistry.set(config.type, config as unknown as BlockConfig);
}

/**
 * Get the full configuration for a block type.
 *
 * @param type - The block type to look up
 * @returns The block config, or undefined if not registered
 */
export function getBlockConfig(type: BlockType): BlockConfig | undefined {
  return blockRegistry.get(type);
}

/**
 * Get the React component for a block type.
 * Convenience function that extracts just the component from the config.
 *
 * @param type - The block type to look up
 * @returns The React component, or undefined if not registered
 */
export function getBlockComponent(type: BlockType) {
  return blockRegistry.get(type)?.component;
}

/**
 * Get all registered block types.
 *
 * @returns Array of all registered block type identifiers
 */
export function getAllBlockTypes(): BlockType[] {
  return Array.from(blockRegistry.keys());
}

/**
 * Get all registered block configurations.
 * Useful for building insert menus or block type selectors.
 *
 * @returns Array of all registered block configs
 */
export function getAllBlockConfigs(): BlockConfig[] {
  return Array.from(blockRegistry.values());
}

/**
 * Get block configurations that should appear in the "Add block" menu.
 *
 * Blocks are shown in the menu by default. Set `showInAddBlockMenu: false`
 * in a block config to hide it from "Add block" UI entry points.
 */
export function getAddBlockMenuConfigs(): BlockConfig[] {
  return getAllBlockConfigs().filter((config) => config.showInAddBlockMenu !== false);
}
